import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Devis, FactureFormalite } from '@europrocurement/l2d-domain';
import { useBilling } from '../hooks';
import BillingContext from './BillingContext';
import { useSelectedDossierContext } from './SelectedDossierContext';

interface BillingProviderProps {
    children: ReactNode;
    origine?: 'L2DF' | 'B2D';
}
const BillingProvider: React.FunctionComponent<BillingProviderProps> = function ({
    children,
    origine = 'L2DF',
}) {
    const { getEstimates, getInvoices } = useBilling({ origine });
    const { dossier } = useSelectedDossierContext();
    const [estimates, setEstimates] = useState<Array<Devis>>([]);
    const [reload, setReload] = useState<boolean>(false);
    const [actif, setActif] = useState<boolean | undefined>(true);
    const [invoices, setInvoices] = useState<Array<FactureFormalite>>([]);
    const [estimatesLoaded, setEstimatesLoaded] = useState<boolean>(false);
    const [invoicesLoaded, setInvoicesLoaded] = useState<boolean>(false);

    useEffect(() => {
        if (dossier.collectionId !== undefined) {
            setEstimatesLoaded(false);
            setInvoicesLoaded(false);
            getEstimates(
                dossier.collectionId as unknown as number,
                actif === true ? true : undefined,
            )
                .then(setEstimates)
                .catch(() => {
                    setEstimates([]);
                    console.error('An error occurred');
                })
                .finally(() => setEstimatesLoaded(true));
            getInvoices(dossier.collectionId as unknown as number)
                .then(setInvoices)
                .catch(() => {
                    setInvoices([]);
                    console.error('An error occurred');
                })
                .finally(() => setInvoicesLoaded(true));
        }
        setReload(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier, reload]);

    const updateActif = useCallback(
        (actifDevis: boolean | undefined) => {
            if (origine === 'B2D') {
                setActif(actifDevis);
                setReload(true);
            }
        },
        [origine],
    );

    const contextValue = useMemo(
        () => ({
            estimates,
            invoices,
            estimatesLoaded,
            invoicesLoaded,
            setReload,
            updateActif,
            actif,
            setActif,
        }),
        [
            estimates,
            invoices,
            estimatesLoaded,
            invoicesLoaded,
            setReload,
            updateActif,
            actif,
            setActif,
        ],
    );

    return <BillingContext.Provider value={contextValue}>{children}</BillingContext.Provider>;
};

export default BillingProvider;
